import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '@mui/material'
import { GridApi, GridRenderCellParams, GridRowId } from '@mui/x-data-grid-premium'
import { Box, Stack, IconButton, Typography, Button, Menu, Divider, MenuItem, ListItemIcon, ListItemText } from '@vapor/material'
import { LocalizedString, useConfirm } from 'hic-components'
import React, { useState, useCallback, useMemo, useRef, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductionAssignedBillPageFragment, ReservedResourcePhase, useDeleteBillMutation } from 'src/api/api.generated'
import { Currency } from 'src/api/currency'
import { UpdateCoParameter } from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { sumAmounts } from 'src/utils/currency-amount'
import { AssignedProductionF, BillF } from '../../../../types/reservation-detail.types'
import { getCurrentResourceOccupancy } from '../../../../utils/resource-occupancy'
import { displayResourceType } from '../../../../../../../../utils/resource-type'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import {
  faArrowRightArrowLeft,
  faHashtag,
  faHouse,
  faSplit,
  faTrash,
  faUpRightFromSquare,
  faUsers,
  faBed,
  faCartShopping,
  faChevronDown,
  faChevronUp,
  faLock,
  faEllipsisV,
  faUser,
  faPlus,
  faCashRegister,
} from '@fortawesome/pro-regular-svg-icons'
import { ProductionAssignedActionType } from '../productions-assigned-actions/production-assigned-actions.type'
import { HicChip } from 'src/app/components/hic-chip/hic-chip'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { Bill } from 'src/app/pages/bills/utils'
import { Action, DeleteAction } from 'src/app/components/data-grid-action-cell-item'
import { HiCIcon } from 'src/app/components/icons/icon'

export type BillGroupCellProps = {
  apiRef: React.MutableRefObject<GridApi>
  params: GridRenderCellParams<any, any, any>
  bill: BillF | Bill
  onDocumentOpen: (params: UpdateCoParameter) => void
  onAddNewProduction: () => void
  readOnly?: boolean
  selectedProductionAssignedIds?: string[]
  onAction: (action: ProductionAssignedActionType, rowId: GridRowId[]) => void
  onExpansionEvent: (rowId: GridRowId, expanded: boolean) => void
  isInsideBillPage?: boolean
  onOpenReservation?: (reservationId: string) => void
  onAddProduction: (bill: Bill | BillF) => void
}

type RO = {
  start: Date
  end: Date
  phase: ReservedResourcePhase
  reservedResourceType: {
    name: {
      description: LocalizedString
    }
    code?: string | null
    customName?: LocalizedString | null
  }
  resource?:
    | {
        code: string
      }
    | null
    | undefined
}
type RR = {
  id: string
  resourceOccupancies: RO[]
}

export default function BillGroupCell({
  apiRef,
  params,
  bill,
  onDocumentOpen,
  onAddNewProduction,
  readOnly,
  selectedProductionAssignedIds,
  onAction,
  isInsideBillPage,
  onOpenReservation,
  onExpansionEvent,
  onAddProduction,
}: BillGroupCellProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const confirm = useConfirm()
  const showError = useErrorSnackBar()
  const { id: tenantId } = useTenant()

  const [deleteBill] = useDeleteBillMutation()
  const formatDate = useFormatDate()
  const ownerName = 'owner' in bill ? bill.owner?.name : bill.searchInfo.ownerName

  const [addChargeMenu, setAddChargeMenu] = useState<HTMLButtonElement | null>(null)
  const addChargeMenuOpen = addChargeMenu !== null
  const [selectionBillMenuAnchorEl, setSelectionBillMenuAnchorEl] = useState<HTMLButtonElement | null>(null)
  const billMenuOpen = selectionBillMenuAnchorEl !== null

  const onExpandClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (params.rowNode.type === 'group') {
        apiRef.current.setRowChildrenExpansion(params.id, !params.rowNode.childrenExpanded)
        apiRef.current.setCellFocus(params.id, params.field)
        onExpansionEvent(params.id, !params.rowNode.childrenExpanded)
        event.stopPropagation()
      }
    },
    [apiRef, params],
  )

  const hotelDate = useHotelDate()
  const localizeString = useLocalizeString()

  const resources: string[] = useMemo(() => {
    if ('reservedResource' in bill) {
      const rrs = bill.reservedResource || []

      const ros: RO[] = rrs.reduce((prev: RO[], current: RR | null | undefined) => {
        const ro = current && getCurrentResourceOccupancy<RR, RO>(hotelDate, current)
        return ro ? [...prev, ro] : prev
      }, [])

      const displays = ros.map((ro) => {
        const resourceType = displayResourceType(localizeString, ro.reservedResourceType)
        const resourceCode = ro?.resource?.code
        return `${resourceCode || resourceType}`
      })

      return displays
    } else {
      return bill.searchInfo.resources ?? []
    }
  }, [bill])

  const productionSums = useMemo(() => {
    // check needed because in the console it was throwing an error
    // This expression is not callable. Each member of the union type ... has signatures, but none of those signatures are compatible with each other.
    if ('ownerId' in bill) {
      const productions = bill.productions ?? []
      const amounts = getAmounts(productions)
      const sum = sumAmounts(amounts)
      return sum
    } else {
      const productions = bill.productions ?? []
      const amounts = getAmounts(productions)
      const sum = sumAmounts(amounts)
      return sum
    }
  }, [bill])

  const { formatCurrency } = useFormatCurrency()

  const billCancelAction = (billId: string) => {
    confirm({
      title: t('pms.booking.reservation.bill_delete_confirm.title'),
      message: t('pms.booking.reservation.bill_delete_confirm.message'),
      cancelButtonText: t('pms.booking.reservation.bill_delete_confirm.cancel_button'),
      confirmButtonText: t('pms.booking.reservation.bill_delete_confirm.confirm_button'),
      onConfirm: () => {
        deleteBill({
          variables: {
            deleteBillTenantId: tenantId,
            deleteBillId: billId,
          },
          refetchQueries: ['ReservationDetail'],
          onError: (error) => {
            showError(error.message)
          },
        })
      },
      onCancel: () => setSelectionBillMenuAnchorEl(null),
    })
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const checkOverflow = () => {
    const container = containerRef.current
    const content = containerRef.current
    if (container && content) {
      return container.scrollWidth > content.clientWidth
    }
  }

  const checkin = useMemo(() => {
    if ('reservation' in bill) {
      return bill?.productions?.reduce(
        (a: ProductionAssignedBillPageFragment | null, b: ProductionAssignedBillPageFragment | null) =>
          a?.production?.reservedResource && b?.production?.reservedResource
            ? a.production.reservedResource.start < b.production.reservedResource.start
              ? a
              : b
            : a?.production?.reservedResource
            ? a
            : b?.production?.reservedResource
            ? b
            : null,
        null,
      )?.production?.reservedResource?.start
    }
  }, [bill])

  const checkout = useMemo(() => {
    if ('reservation' in bill) {
      return bill?.productions?.reduce(
        (a: ProductionAssignedBillPageFragment | null, b: ProductionAssignedBillPageFragment | null) =>
          a?.production?.reservedResource && b?.production?.reservedResource
            ? a.production.reservedResource.end > b.production.reservedResource.end
              ? a
              : b
            : a?.production?.reservedResource
            ? a
            : b?.production?.reservedResource
            ? b
            : null,
        null,
      )?.production?.reservedResource?.end
    }
  }, [bill])

  return (
    <Stack spacing={0.25} direction="row" sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center', px: 0, mx: 0 }}>
      <IconButton size="small" onClick={onExpandClick}>
        <FontAwesomeIcon
          size="xs"
          icon={params.rowNode.type === 'group' && params.rowNode.childrenExpanded ? faChevronUp : faChevronDown}
          color={theme.palette.secondary.main}
          style={{
            fontSize: '0.875rem',
          }}
        />
      </IconButton>

      <div style={{ display: 'flex', overflow: 'hidden', position: 'relative' }} ref={containerRef}>
        <div ref={contentRef}>
          <Stack sx={{ display: 'flex' }}>
            {checkOverflow() && <div className="billEllipsis"></div>}
            <Stack direction="row" spacing={1} alignItems={'center'}>
              {!bill.open && <FontAwesomeIcon icon={faLock} />}
              <Typography variant="titleXSmall" title={bill.description} sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bill.description}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} height={18} sx={{ pb: 0.5, dispaly: 'flex' }}>
              <HicChip text={bill.category.toString()} variant="light" />
              <HicChip icon={faUser} text={ownerName} variant="light" />
              {resources.map((r, index) => (
                <HicChip key={index} icon={faBed} text={r} variant="light" />
              ))}
              {bill.reservedResourceId === null && resources.length <= 0 && <HicChip icon={faBed} text={t('pms.booking.reservation.bill.all_rooms')} variant="light" />}
              {productionSums.map((p, index) => (
                <HicChip key={index} icon={faCartShopping} text={formatCurrency(p.amount, p.currency)} variant="light" />
              ))}
              {isInsideBillPage &&
                'reservation' in bill &&
                (bill.reservation ?? []).map((reservation) => {
                  if (!reservation) return null

                  return (
                    <Fragment key={reservation.id}>
                      <HicChip text={reservation.reservationCode} variant="light" icon={faHashtag} />
                      {reservation.isGroup && reservation.group && <HicChip text={reservation.group} variant="light" icon={faUsers} />}
                      {checkin && checkout && <HicChip text={`${formatDate(checkin)} - ${formatDate(checkout)}`} variant="light" icon={faHouse} />}
                    </Fragment>
                  )
                })}
            </Stack>
          </Stack>
        </div>
      </div>

      <Stack direction="row" alignItems="center" spacing={1} style={{ marginLeft: 'auto' }}>
        {!isInsideBillPage && 'document' in bill && bill.document?.numberExtended && (
          <Box sx={{ background: 'white' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={readOnly}
              onClick={() => {
                const documentId = bill.document?.id
                if (documentId) {
                  onDocumentOpen({ documentId })
                }
              }}
            >
              {bill.document.numberExtended}
            </Button>
          </Box>
        )}
        {'reservationId' in bill &&
          onOpenReservation &&
          bill.reservationId?.map((reservationId) => {
            if (!reservationId) return null

            return (
              <Button
                key={reservationId}
                variant="outlined"
                size="small"
                onClick={() => {
                  onOpenReservation(reservationId)
                }}
                sx={{ background: 'white' }}
                disabled={readOnly}
                startIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
              >
                {t('pms.booking.reservation.bills_action.open_reservation')}
              </Button>
            )
          })}
        {bill.open && (
          <Box sx={{ background: 'white', minWidth: '90px' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                setAddChargeMenu(event.currentTarget)
              }}
              disabled={readOnly}
              fullWidth
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('pms.booking.reservation.bills_action.add_production')}
            </Button>
          </Box>
        )}
        <Menu
          open={addChargeMenuOpen}
          anchorEl={addChargeMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={() => {
            setAddChargeMenu(null)
          }}
        >
          <MenuItem
            onClick={() => {
              onAddNewProduction()
              setAddChargeMenu(null)
            }}
          >
            <ListItemIcon>
              <HiCIcon>
                <FontAwesomeIcon icon={faCashRegister} />
              </HiCIcon>
            </ListItemIcon>
            <ListItemText>{t('pms.booking.reservation.bills_action.add_production.from_pos')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onAddProduction(bill)
              setAddChargeMenu(null)
            }}
          >
            <ListItemIcon>
              <HiCIcon>
                <FontAwesomeIcon icon={faPlus} />
              </HiCIcon>
            </ListItemIcon>
            <ListItemText>{t('pms.booking.reservation.bills_action.add_production.custom')}</ListItemText>
          </MenuItem>
        </Menu>
        <IconButton //
          disabled={!bill.open || readOnly}
          color="primary"
          size="small"
          onClick={(e) => setSelectionBillMenuAnchorEl(e.currentTarget)}
        >
          <FontAwesomeIcon icon={faEllipsisV} fontSize={'1rem'} />
        </IconButton>
        <Menu
          open={billMenuOpen}
          anchorEl={selectionBillMenuAnchorEl}
          onClose={() => setSelectionBillMenuAnchorEl(null)}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <Stack sx={{ py: 1 }}>
            <Typography variant="pageDescription" pl={2}>
              {t('pms.booking.reservation.productions_assigned.menu.production_action_group_title').toUpperCase()}
            </Typography>
            <Action
              icon={faArrowRightArrowLeft}
              disabled={!selectedProductionAssignedIds || !selectedProductionAssignedIds.length ? true : false}
              onClick={() => {
                onAction('MOVE', selectedProductionAssignedIds ?? [])
              }}
              label={t('pms.booking.reservation.productions_assigned.action.move.title')}
              showInMenu
            />
            <Action
              icon={faTrash}
              disabled={!selectedProductionAssignedIds || !selectedProductionAssignedIds.length ? true : false}
              onClick={() => {
                onAction('DELETE', selectedProductionAssignedIds ?? [])
              }}
              label={t('pms.booking.reservation.productions_assigned.action.delete.title')}
              showInMenu
            />
            <Action
              icon={faSplit}
              disabled={!selectedProductionAssignedIds || !selectedProductionAssignedIds.length ? true : false}
              onClick={() => {
                onAction('SPLIT', selectedProductionAssignedIds ?? [])
              }}
              label={t('pms.booking.reservation.productions_assigned.action.split.title')}
              showInMenu
            />
            <Divider light />
            <DeleteAction label={t('pms.booking.reservation.bill.delete')} onClick={() => billCancelAction(bill.id)} showInMenu />
          </Stack>
        </Menu>
      </Stack>
    </Stack>
  )
}

function getAmounts(productions: (AssignedProductionF | ProductionAssignedBillPageFragment)[]) {
  const amounts = productions.reduce((prev: { amount: number; currency: Currency }[], current) => {
    const amount = { amount: current.production.amount * current.quota * (current.production.quantity ?? 1), currency: current.production.currency }
    return [...prev, amount]
  }, [])
  return amounts
}
